import { useRef, useState } from "react";
import { ButtonTileProps, Tile } from "../Tile";
import { ReactComponent as DownChevron } from "../../svg/chevron-down.svg";
import { Popover } from "../Popover";
import { Card } from "../Card";
import styles from "./PopoverTile.module.scss";
import { TagAction } from "../../types/Tag.types";
import { Link } from "../Link";

export type CallToAction = {
  label: string;
  href?: string;
  tagAction?: TagAction;
  isLoading?: boolean;
  onClick?: () => void;
};

export type PopoverTileProps = Omit<ButtonTileProps, "onClick"> & {
  ctaList?: CallToAction[];
  onClick?: () => void;
};

/**
 * Defines a tile that will show a popup menu when clicked if it has a cta list defined.
 * Otherwise onClick() is called when the tile is clicked.
 */
export function PopoverTile(props: PopoverTileProps) {
  const triggerRef = useRef<HTMLElement>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const hasMultipleCtas = (props.ctaList?.length ?? 0) > 0;

  const hidePopover = () => {
    setIsPopoverOpen(false);
    triggerRef?.current?.blur();
  };

  return (
    <>
      <Tile
        icon={props.icon}
        isLoading={props.isLoading}
        className={props.className}
        cta={props.cta}
        ctaIcon={hasMultipleCtas ? <DownChevron /> : props.ctaIcon}
        ariaLabel={props.ariaLabel}
        tagAction={props.tagAction}
        onClick={() => {
          if (hasMultipleCtas) setIsPopoverOpen(!isPopoverOpen);
          else props.onClick?.();
        }}
        ref={triggerRef}
      />
      {hasMultipleCtas && (
        <Popover
          triggerRef={triggerRef}
          onClose={() => setTimeout(hidePopover)}
          isOpen={isPopoverOpen}
          placement={"bottom start"}
          className={styles.popover}
          restoreFocus={false}
          style={{ width: "unset", maxWidth: "unset" }}
        >
          <Card className={styles.card}>
            {props.ctaList?.map((cta) => (
              <Link
                key={cta.label}
                href={cta.href ?? "#"}
                tagAction={cta.tagAction}
                isLoading={cta.isLoading}
                onClick={(e) => {
                  if (!cta.href) e.preventDefault();
                  hidePopover();
                  cta.onClick?.();
                }}
              >
                {cta.label}
              </Link>
            ))}
          </Card>
        </Popover>
      )}
    </>
  );
}
